import { memo } from 'react';
import { Box, Flex, Heading, Text, VStack, useBreakpointValue } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import RatingEmoji from '@/components/feedback/RatingEmoji';
import { RATING_EMOJIS, getRatingGlowColor } from '@/utils/restaurantUtils';
import { CATEGORY_INFO, FOCUS_RING } from '../ratingCategories';
import type { RatingCategory } from '../hooks/useRestaurantRating';

const MotionBox = motion.create(Box);

interface CategoryRatingStepProps {
  /** The category being rated */
  category: RatingCategory;
  /** Current rating value (0 if not rated) */
  value: number;
  /** Callback when a rating is selected — must be a stable reference. */
  onSelect: (value: number) => void;
}

/**
 * Individual category rating card: icon, question-form heading, subtitle,
 * and a single row of all 6 emoji (always one row, mobile and desktop
 * alike — this deliberately does not collapse to multiple rows at any
 * viewport). Icon size is responsive (`useBreakpointValue`) so the row
 * always fits without wrapping or horizontal scrolling — larger icons with
 * always-visible color rings, no text labels; per-level meaning is not
 * currently surfaced elsewhere in the UI — CD removed the explainer panel
 * that used to carry it, see paire-wiki's "Unified Emoji Rating Scale" note.
 *
 * Selection is a plain tap/click/keyboard-activate on a single emoji button,
 * landing on `onSelect(value)`. (A press-and-drag slide-to-select gesture
 * was tried here and reverted — it conflicted with the app's existing
 * swipe-to-navigate-between-categories gesture on mobile, since a drag
 * across this row and a swipe across the screen are the same input.)
 *
 * Memoized: the parent booth stays mounted at `display:none` while the
 * member is on the dish carousel, so without memoization every unrelated
 * `setMenuItems` call would re-render this card for nothing. The parent
 * must pass a stable `onSelect` (useCallback keyed on `category`) or the
 * memoization is defeated regardless of this component's own comparison.
 */
function CategoryRatingStepImpl({ category, value, onSelect }: CategoryRatingStepProps) {
  const info = CATEGORY_INFO.find((c) => c.category === category)!;

  // Smaller icons on narrow viewports so all 6 fit one row without wrapping
  // or overflowing; a larger (but not the largest) size is restored at md+.
  // `3xl` was tried here and measured to overflow: at `3xl` (64px) with
  // ringMultiplier=1.1 each ring is ~70.4px, each button ~78.4px wide
  // (ring + 8px of button padding), so 6 buttons + 5 `md` gaps (16px each)
  // need ~550px — more than the ~512px actually available inside the
  // modal's 560px `contentMaxW` (RestaurantRatingModal) once the row's own
  // ancestor padding is subtracted. That overflow was hard-clipped by
  // RatingStep's `overflow="hidden"` wrapper, cutting off the rightmost
  // ("Highest") emoji's ring. `2xl` (48px) keeps desktop icons meaningfully
  // larger than mobile while the same row totals ~445px — comfortably
  // inside the 512px available, no clipping. See RatingEmoji.tsx for the
  // size-to-pixel map.
  // Falls back to the smaller size if the breakpoint hasn't resolved yet on
  // first paint — avoids a flash of oversized icons wrapping/overflowing on
  // a narrow viewport before `useBreakpointValue` settles.
  const emojiSize = useBreakpointValue({ base: 'xl', md: '2xl' } as const, { ssr: false }) ?? 'xl';

  return (
    <VStack spacing={6} align="center" w="100%" px={2}>
      {/* Category header */}
      <VStack spacing={2} textAlign="center" px={2}>
        <Text fontSize="3xl" aria-hidden="true">{info.icon}</Text>
        <Heading
          id={`category-rating-heading-${category}`}
          tabIndex={-1}
          size="lg"
          color="white"
          fontWeight="bold"
          data-testid={`rating-category-${category}`}
        >
          {info.heading}
        </Heading>
        <Text color="gray.400" fontSize="md">
          {info.subtitle}
        </Text>
      </VStack>

      {/*
        Single row, all 6 emoji, every viewport. Icon size shrinks on narrow
        viewports (see `emojiSize` above) so this never wraps or scrolls.
      */}
      <Flex
        justify="space-between"
        align="center"
        w="100%"
        gap={{ base: 1, md: 4 }}
        role="radiogroup"
        aria-label={info.heading}
      >
        {RATING_EMOJIS.map((emoji) => {
          const isSelected = value === emoji.value;
          const glowColor = getRatingGlowColor(emoji.value);

          return (
            <MotionBox
              key={emoji.value}
              whileTap={{ scale: 0.92 }}
              whileHover={{ scale: 1.05 }}
              display="flex"
              justifyContent="center"
            >
              <Box
                as="button"
                type="button"
                role="radio"
                onClick={() => onSelect(emoji.value)}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minW="44px"
                minH="44px"
                py={2}
                px={1}
                borderRadius="var(--r-card-compact)"
                bg="transparent"
                border="none"
                cursor="pointer"
                transition="all 0.2s ease-out"
                aria-label={`${emoji.label} — ${info.heading}`}
                aria-checked={isSelected}
                opacity={isSelected ? 1 : 0.85}
                _hover={{
                  opacity: 1,
                  '& > div:first-of-type': {
                    filter: `drop-shadow(0 0 8px ${glowColor})`,
                  },
                }}
                _focusVisible={FOCUS_RING}
                data-testid={`rating-emoji-${category}-${emoji.value}`}
              >
                <Box
                  transform={isSelected ? 'scale(1.15)' : 'scale(1)'}
                  filter={isSelected ? `drop-shadow(0 0 12px ${glowColor})` : 'none'}
                  transition="all 0.2s ease-out"
                >
                  <RatingEmoji
                    emoji={emoji}
                    size={emojiSize}
                    showBackground
                    ringMultiplier={1.1}
                  />
                </Box>
              </Box>
            </MotionBox>
          );
        })}
      </Flex>
    </VStack>
  );
}

export const CategoryRatingStep = memo(CategoryRatingStepImpl);
export default CategoryRatingStep;
